import type { AgentHitlGate, SlaveCompleteEvent, StreamEvent } from '../types';
import type { StreamNotice } from '../components/usage/WalletBanner';

export const applySlaveComplete = (event: StreamEvent): SlaveCompleteEvent => ({
  type: 'slave_complete',
  cost_usd: event.cost_usd,
  num_turns: event.num_turns,
  duration_ms: event.duration_ms,
  session_id: event.session_id,
  terminal_reason: event.terminal_reason,
  prompt_group_id: event.prompt_group_id,
  tokens: event.tokens,
  models: event.models,
});

export const walletNoticeFromEvent = (event: StreamEvent): StreamNotice | null => {
  if ((event.type === 'warning' || event.type === 'error') && event.code && event.code.startsWith('balance_')) {
    return { code: event.code, message: event.message || '', used_pct: event.used_pct };
  }
  return null;
};

/** MCP tools that block on a HITL prompt; keep in sync with HITL_TOOL_NAMES
 *  in qa_agent/mcp/tools/hitl.py. */
export const HITL_TOOLS = ['request_user_input', 'security_create_issue'] as const;

export const isHitlTool = (toolName: string): boolean =>
  HITL_TOOLS.some((name) => toolName.includes(name));

/** Tools whose prompt is composed server-side: the gate loads it from
 *  GET /chat/sessions/{id}/hitl-pending instead of the tool-start input. */
export const isServerComposedHitlTool = (toolName: string): boolean =>
  toolName.includes('security_create_issue');

interface ToolStartLike {
  tool?: string;
  tool_use_id?: string;
  input?: string;
}

/** The HITL gate a slave_tool_start opens, or null when it opens none. */
export const hitlGateFromToolStart = (event: ToolStartLike, sessionId: string): AgentHitlGate | null => {
  const tool = event.tool || '';
  if (isServerComposedHitlTool(tool)) {
    return {
      requestId: '',
      sessionId,
      eventType: 'security_create_issue',
      message: 'Preparing the approval request…',
      data: { pending: true },
      toolUseId: event.tool_use_id || '',
    };
  }
  if (!tool.includes('request_user_input') || !event.input) return null;
  try {
    const hitlInput = JSON.parse(event.input);
    return {
      requestId: hitlInput.data?.request_id || event.tool_use_id || '',
      sessionId,
      eventType: hitlInput.event_type || 'confirmation_required',
      message: hitlInput.message || 'The agent needs your input.',
      data: hitlInput.data || {},
      toolUseId: event.tool_use_id || '',
      interactionRequired: !!hitlInput.interaction_required,
    };
  } catch {
    return null;
  }
};

export interface PendingHitlRequest {
  request_id: string;
  event_type: string;
  message: string;
  data: Record<string, any>;
}

/** Poll the session's pending HITL requests until one of `eventType` appears. */
export const waitForPendingHitl = async (
  fetchPending: () => Promise<PendingHitlRequest[]>,
  eventType: string,
  opts: {
    attempts?: number;
    intervalMs?: number;
    isCancelled?: () => boolean;
    /** Extra filter, e.g. to pin one request id / tool. */
    match?: (r: PendingHitlRequest) => boolean;
  } = {},
): Promise<PendingHitlRequest | null> => {
  const attempts = opts.attempts ?? 60;
  const intervalMs = opts.intervalMs ?? 1000;
  for (let i = 0; i < attempts; i += 1) {
    if (opts.isCancelled?.()) return null;
    try {
      const found = (await fetchPending()).filter(
        (r) => r.event_type === eventType && (opts.match ? opts.match(r) : true),
      );
      if (found.length) return found[found.length - 1];
    } catch {
      /* transient — retry */
    }
    if (i < attempts - 1) await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return null;
};

/** Tools whose over-cap calls ask for a `load_large` approval mid-call (see
 *  qa_agent/mcp/tools/load_cap.py). They open NO gate on tool start: a card
 *  shows only if the server registers a pending `load_large` request. */
export const LOAD_CAP_TOOLS = ['load_run', 'load_upsert_test', 'load_schedule'] as const;

export const isLoadCapTool = (toolName: string): boolean =>
  LOAD_CAP_TOOLS.some((name) => toolName.includes(name));

/** Which capped tool a tool name is (`load_run`, ...), or '' for none. */
export const loadCapToolKey = (toolName: string): string =>
  LOAD_CAP_TOOLS.find((name) => toolName.includes(name)) || '';

/** Is this pending request a fresh `load_large` card for exactly this tool,
 *  one that was not already pending, claimed by another watcher or open? */
export const isOwnLoadLargeRequest = (
  r: PendingHitlRequest,
  toolKey: string,
  ignore: ReadonlySet<string>,
): boolean =>
  r.event_type === 'load_large' && !ignore.has(r.request_id) && r.data?.tool === toolKey;

/** The approve / decline gate for a pending `load_large` request. */
export const loadLargeGate = (
  pending: PendingHitlRequest,
  sessionId: string,
  toolUseId: string,
): AgentHitlGate => ({
  requestId: pending.request_id,
  sessionId,
  eventType: 'load_large',
  message: pending.message,
  data: pending.data || {},
  toolUseId,
});
